import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import type { z } from 'zod'
import type { billSchema } from '@super-mega/server/contracts/bill'
import { useTRPC } from '@/trpc/react.ts'
import { billSelectionError, type BillSelectionMode } from '../utils/bill-selection.ts'
import { refreshMoneyQueries } from '../utils/refresh-money-queries.ts'

type Bill = z.infer<typeof billSchema>

export function useIssuedBills(initialBills: Bill[], mode: BillSelectionMode, includedIds?: number[]) {
  const [allBills, setBills] = useState(initialBills)
  const includedSet = includedIds ? new Set(includedIds) : undefined
  const bills = includedSet ? allBills.filter((bill) => includedSet.has(bill.id)) : allBills
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const error = billSelectionError(bills, mode)
  const drafts = bills.filter((bill) => bill.status === 'draft')
  // Query 会等待 onSuccess，isPending 同时覆盖出账和刷新，避免维护第二份提交状态。
  const batch = useMutation(
    trpc.bill.batchIssue.mutationOptions({
      // 固定提交时的账单范围，刷新期间切换选择不会扩大这次操作的范围。
      onMutate: () => bills,
      onSuccess: async (results, _input, selectedBills) => {
        await refreshMoneyQueries(cache, trpc)
        const fresh = await Promise.all(
          selectedBills.map((bill) =>
            cache.fetchQuery(trpc.bill.detail.queryOptions({ billId: bill.id }, { staleTime: 0 }))
          )
        )
        const refreshedBills = new Map(fresh.map((bill) => [bill.id, bill]))
        setBills((previous) => previous.map((bill) => refreshedBills.get(bill.id) ?? bill))
        const failures = results.filter((result) => !result.success)
        if (failures.length) {
          void message.error(failures.map((result) => `#${result.billId} ${result.message}`).join('；'), 10)
        } else {
          void message.success('账单已确认出账，可以继续保存')
        }
      },
      onError: (cause) => {
        void message.error(cause instanceof Error ? cause.message : '确认出账失败')
      }
    })
  )
  const issue = () => {
    if (batch.isPending) return Promise.resolve()
    return batch
      .mutateAsync({ bills: drafts.map((bill) => ({ billId: bill.id, expectedUpdatedAt: bill.updatedAt })) })
      .catch(() => undefined)
  }
  return { bills, allBills, error, drafts, issue, issuing: batch.isPending, ready: !error && !drafts.length }
}
